import { adminCaptureException } from '@diamond/shared/utils';
import { isAxiosError } from 'axios';

export type LhcApiErrorExtras = {
  lhcId?: string;
  lhc_no?: string;
  itemId?: string;
  bank_mutation_ids?: string[];
  mutation_nos?: string[];
  mutationId?: string;
  paymentProofId?: string;
  orderMetaId?: string;
  amount_applied?: number;
  allocations?: Array<{ bank_mutation_id: string; amount_applied: number }>;
  searchQuery?: string;
  plant?: string;
  collection_plant_id?: string;
  plant_id?: string;
  lhc_date?: string;
  deliveryOrderIds?: string[];
  poNumber?: string;
  excludeDeliveryOrderId?: string;
  index?: number;
  id?: string;
  params?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  sap_no_bilto?: string;
  hasRemark?: boolean;
  invoiceNo?: string;
  deliveryOrderNo?: string;
};

export type CaptureLhcApiErrorOptions = {
  action: string;
  extras?: LhcApiErrorExtras;
};

type ApiErrorBody = {
  message?: string;
  statusCode?: number;
};

function stripUndefined(
  extras: LhcApiErrorExtras | undefined
): Record<string, unknown> {
  if (!extras) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(extras).filter(([, value]) => value !== undefined)
  );
}

function getErrorPayload(error: unknown): {
  message: string;
  statusCode: number;
  respData: ApiErrorBody;
} {
  if (isAxiosError<ApiErrorBody>(error)) {
    const respData = error.response?.data ?? {};
    return {
      message:
        respData.message ??
        (error instanceof Error ? error.message : 'Unknown error'),
      statusCode:
        respData.statusCode ??
        (typeof error.response?.status === 'number' ? error.response.status : 0),
      respData,
    };
  }

  return {
    message: error instanceof Error ? error.message : 'Unknown error',
    statusCode: 0,
    respData: {},
  };
}

export function captureLhcApiError(
  error: unknown,
  options: CaptureLhcApiErrorOptions
): void {
  const { action, extras } = options;
  const { message, statusCode, respData } = getErrorPayload(error);
  const cleanedExtras = stripUndefined(extras);

  adminCaptureException(
    {
      message,
      statusCode,
    },
    (scope) => {
      scope.setTags({
        feature: 'collection-management',
        action,
        status_code: statusCode,
      });
      scope.setContext('error', {
        error: respData,
      });
      if (Object.keys(cleanedExtras).length > 0) {
        scope.setExtra('lhc', cleanedExtras);
      }
    }
  );
}
